import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
var _class;
import { action, observable, when } from 'mobx';
export const MAX_CONCURRENT_PANEL_LOADS = 8;
function comparePriority(a, b) {
  return a[0] - b[0] || a[1] - b[1] || a[2] - b[2];
}
let BoardLoadScheduler = (_class = class BoardLoadScheduler {
  constructor({
    shouldLoadAll = () => false,
    maxConcurrent = MAX_CONCURRENT_PANEL_LOADS
  } = {}) {
    this.registered = new Map();
    this.running = new Map();
    this.pending = observable.set();
    this.inView = observable.set();
    this.shouldLoadAll = void 0;
    this.maxConcurrent = void 0;
    this.dispatchTimer = void 0;
    this.shouldLoadAll = shouldLoadAll;
    this.maxConcurrent = maxConcurrent;
  }
  isPending(id) {
    return this.pending.has(id);
  }
  isRegistered(id) {
    return this.registered.has(id);
  }
  register(id, entry) {
    this.registered.set(id, entry);
    this.requestDispatch();
    return () => this.unregister(id);
  }
  unregister(id) {
    this.registered.delete(id);
    this.pending.delete(id);
    this.inView.delete(id);
    this.running.get(id)?.();
    this.running.delete(id);
    this.requestDispatch();
  }
  setInView({
    id,
    isInView
  }) {
    if (isInView) {
      this.inView.add(id);
      this.requestDispatch();
    } else {
      this.inView.delete(id);
    }
  }

  // may arrive before register() (child effects run first); waits for registration
  schedule(id) {
    const entry = this.registered.get(id);
    if (entry && this.running.has(id) && this.shouldLoad(id)) {
      // already holds a slot; re-run in place instead of re-queuing
      this.pending.delete(id);
      entry.run();
    } else {
      // the queued re-run supersedes the current subscription
      entry?.cancel();
      this.pending.add(id);
      this.requestDispatch();
    }
  }
  reset() {
    clearTimeout(this.dispatchTimer);
    this.dispatchTimer = undefined;
    this.running.forEach(dispose => dispose());
    this.running.clear();
    this.pending.clear();
    this.inView.clear();
  }
  shouldLoad(id) {
    return this.shouldLoadAll() || this.inView.has(id);
  }

  // requests arrive one panel at a time; batching lets each pass rank all eligible panels
  requestDispatch() {
    if (this.dispatchTimer === undefined) {
      this.dispatchTimer = setTimeout(() => {
        this.dispatchTimer = undefined;
        this.dispatch();
      });
    }
  }
  dispatch() {
    const freeSlots = this.maxConcurrent - this.running.size;
    if (freeSlots <= 0) {
      return;
    }
    const next = [...this.pending].filter(id => this.registered.has(id) && !this.running.has(id) && this.shouldLoad(id)).map(id => ({
      id,
      priority: this.registered.get(id).getPriority()
    })).sort((a, b) => comparePriority(a.priority, b.priority)).slice(0, freeSlots);
    next.forEach(({
      id
    }) => this.start(id));
  }
  start(id) {
    this.pending.delete(id);
    const entry = this.registered.get(id);
    entry.run();
    let settled = false;
    const dispose = when(() => !entry.isRunning(), () => {
      settled = true;
      this.running.delete(id);
      this.dispatch();
    });

    // `when` can settle synchronously outside an action batch
    if (!settled) {
      this.running.set(id, dispose);
    }
  }
}, _applyDecoratedDescriptor(_class.prototype, "register", [action], Object.getOwnPropertyDescriptor(_class.prototype, "register"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "unregister", [action], Object.getOwnPropertyDescriptor(_class.prototype, "unregister"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setInView", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setInView"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "schedule", [action], Object.getOwnPropertyDescriptor(_class.prototype, "schedule"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "reset", [action], Object.getOwnPropertyDescriptor(_class.prototype, "reset"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "dispatch", [action], Object.getOwnPropertyDescriptor(_class.prototype, "dispatch"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "start", [action], Object.getOwnPropertyDescriptor(_class.prototype, "start"), _class.prototype), _class);
export { BoardLoadScheduler as default };